import { SxProps, Theme } from "@mui/material";

// Design Tokens
export const authColors = {
  primary: {
    main: "#08519c",
    light: "#2171b5",
    lighter: "#4292c6",
    lightest: "#c6dbef",
    subtle: "#eff3ff",
  },
  background: {
    page: "#f5f7fa",
    card: "#ffffff",
    input: "#ffffff",
    inputHover: "#fafbfc",
  },
  border: {
    default: "#e2e8f0",
    hover: "#cbd5e1",
    focus: "#4292c6",
  },
  shadow: {
    soft: "0 2px 8px rgba(8, 81, 156, 0.08)",
    focusRing: "0 0 0 3px rgba(66, 146, 198, 0.15)",
  },
  text: {
    primary: "#1a365d",
    secondary: "#4a5568",
    muted: "#718096",
    subtle: "#a0aec0",
    placeholder: "#a0aec0",
  },
};

// Layout
export const authPageContainerStyles: SxProps<Theme> = {
  width: "100%",
  minHeight: "100vh",
  display: "flex",
  flexDirection: "row",
};

export const authContentAreaStyles: SxProps<Theme> = {
  flexGrow: 1,
  width: {
    md: "60%",
    lg: "50%",
    xl: "40%"
  },
  bgcolor: authColors.background.page,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
};

// Mobile layout styles
export const mobileContentWrapperStyles: SxProps<Theme> = {
  width: "100%",
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  py: 4,
  gap: 3,
};

export const mobileBrandingContainerStyles: SxProps<Theme> = {
  display: "flex",
  justifyContent: "center",
  width: "100%",
};

export const envInfoContainerStyles: SxProps<Theme> = {
  position: "absolute",
  right: 16,
  bottom: 16,
};

export const sidePanelStyles: SxProps<Theme> = {
  width: {
    md: "50%", 
    lg: "50%", 
    xl: "40%"
  },
  minWidth: "350px",
  maxWidth: "950px",
  p: 2,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  position: "relative",
  overflow: "hidden",
  bgcolor: "common.white",
};

export const brandingContentStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  maxWidth: "450px",
  flexGrow: 1,
};

export const illustrationContainerStyles: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexGrow: 1,
  width: "100%",
  p: 0,
};

export const copyrightContainerStyles: SxProps<Theme> = {
  display: "flex",
  justifyContent: "center",
  pt: 3,
  position: "relative",
  zIndex: 1,
};

// Typography
export const taglineStyles: SxProps<Theme> = {
  color: authColors.text.subtle,
  fontWeight: 400,
  fontSize: "0.875rem",
  letterSpacing: "0.02em",
};

export const taglineLightStyles: SxProps<Theme> = {
  color: "rgba(255, 255, 255, 0.85)",
  fontWeight: 400,
  fontSize: "0.875rem",
  letterSpacing: "0.02em",
};

export const captionLightStyles: SxProps<Theme> = {
  color: "rgba(255, 255, 255, 0.7)",
};

export const headlinePrimaryStyles: SxProps<Theme> = {
  color: authColors.text.primary,
  fontWeight: 700,
  lineHeight: 1.2,
};

export const headlineAccentStyles: SxProps<Theme> = {
  color: authColors.primary.light,
  fontWeight: 600,
  lineHeight: 1.2,
};

export const descriptionStyles: SxProps<Theme> = {
  color: authColors.text.muted,
  fontWeight: 400,
  lineHeight: 1.7,
  fontSize: "0.95rem",
};

export const captionStyles: SxProps<Theme> = {
  color: authColors.text.muted,
};

// Components
export const pillInputWrapperStyles: SxProps<Theme> = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "50px",
    backgroundColor: authColors.background.input,
    transition: "all 0.2s ease",

    "& fieldset": {
      borderColor: authColors.border.default,
      borderWidth: "1px",
      transition: "all 0.2s ease",
    },
    "&:hover": {
      backgroundColor: authColors.background.inputHover,
      "& fieldset": { borderColor: authColors.border.hover },
    },
    "&.Mui-focused": {
      backgroundColor: authColors.background.input,
      "& fieldset": {
        borderColor: authColors.border.focus,
        borderWidth: "1px",
        boxShadow: authColors.shadow.focusRing,
      },
    },
    "&.Mui-disabled": {
      backgroundColor: authColors.background.page,
    },
    "& .MuiOutlinedInput-input": {
      padding: "16px 24px",
      fontSize: "0.95rem",
      "&::placeholder": {
        color: authColors.text.placeholder,
        opacity: 1,
      },
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.95rem",
    color: authColors.text.muted,
    "&.Mui-focused": { color: authColors.primary.main },
  },
  "& .MuiInputLabel-outlined": {
    transform: "translate(24px, 16px) scale(1)",
    "&.MuiInputLabel-shrink": {
      transform: "translate(20px, -9px) scale(0.75)",
    },
  },
};

export const pillButtonStyles: SxProps<Theme> = {
  borderRadius: "50px",
  padding: "14px 32px",
  fontSize: "0.95rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  backgroundColor: authColors.primary.main,
  boxShadow: "none",
  transition: "all 0.2s ease",

  "&:hover": {
    backgroundColor: authColors.primary.light,
    boxShadow: authColors.shadow.soft,
    transform: "translateY(-1px)",
  },
  "&:active": {
    transform: "translateY(0)",
    boxShadow: "none",
  },
};

export const pillToggleGroupStyles: SxProps<Theme> = {
    bgcolor: authColors.background.page,
    borderRadius: "50px",
    padding: "4px",
    "& .MuiToggleButton-root": {
        borderRadius: "50px",
        textTransform: "none",
        fontWeight: 500,
        border: "none",
        color: authColors.text.muted,
        "&.Mui-selected": {
            bgcolor: authColors.background.card,
            color: authColors.primary.main,
            boxShadow: "0 4px 10px rgba(8, 81, 156, 0.4)",
            "&:hover": { bgcolor: authColors.background.card },
        },
        "&:hover": {
            bgcolor: "transparent",
        },
    },
};

export const illustrationImageStyles: SxProps<Theme> = {
  width: "100%",
  maxWidth: "850px",
  height: "auto",   
  maxHeight: "80vh",
  objectFit: "contain",
};

export const linkStyles: SxProps<Theme> = {
  color: authColors.primary.light,
  fontSize: "0.875rem",
  cursor: "pointer",
  "&:hover": { color: authColors.primary.main },
};